<template>
    <div class="bg-gray-50 py-10 px-6 overflow-auto">
      <div class="container mx-auto">
        <h1 class="text-4xl font-bold text-[#085f63] mb-8">{{ $t('dashboard.title') }}</h1>

        <!-- Responsive layout: on large screens, cards left (vertical), chart right; on small screens, stack -->
        <div class="flex flex-col lg:flex-row gap-6 mb-10">
          <!-- Cards: stacked vertically on large screens, horizontal on small screens -->
          <div class="grid grid-cols-1 md:grid-cols-3 gap-6 lg:flex lg:flex-col lg:w-1/3">
            <!-- Credits Left -->
            <div class="bg-white p-6 rounded-2xl shadow-md border-l-8" style="border-color: #facf5a">
              <div class="flex items-center gap-2">
                <img src="@/assets/icons/coin.png" alt="Coin Icon" class="w-6 h-6" />
                <h2 class="text-lg font-semibold text-gray-700">{{ $t('dashboard.creditsLeft') }}</h2>
              </div>
              <p class="text-2xl font-bold text-[#facf5a] mt-2">
                {{ creditsLeft }}
              </p>
              <button
                class="mt-4 px-4 py-2 bg-[#facf5a] text-white font-semibold rounded-lg hover:bg-[#e6ba4f] transition"
                @click="redirectToPricing"
              >
                {{ $t('dashboard.addCredits') }}
              </button>
            </div>

            <!-- Conversations -->
            <div class="bg-white p-6 rounded-2xl shadow-md border-l-8" style="border-color: #49beb7">
              <div class="flex items-center gap-2">
                <img src="@/assets/icons/conversation.png" alt="Conversation Icon" class="w-8 h-8" />
                <h2 class="text-lg font-semibold text-gray-700">{{ $t('dashboard.conversations') }}</h2>
              </div>
              <p class="text-2xl font-bold text-[#49beb7] mt-2">{{ conversationStore.conversations.length }}</p>
            </div>

            <!-- Messages This Month -->
            <div class="bg-white p-6 rounded-2xl shadow-md border-l-8" style="border-color: #085f63">
              <div class="flex items-center gap-2">
                <img src="@/assets/icons/messages.png" alt="Messages Icon" class="w-8 h-8" />
                <h2 class="text-lg font-semibold text-gray-700">{{ $t('dashboard.messagesSent') }}</h2>
              </div>
              <p class="text-2xl font-bold text-[#085f63] mt-2">{{ totalMessages }}</p>
            </div>
          </div>

          <!-- Message Chart -->
          <div class="bg-white rounded-2xl shadow-md p-6 flex-1 lg:w-2/3">
            <h3 class="text-xl font-semibold text-gray-700 mb-4">{{ $t('dashboard.monthlyMessageActivity') }}</h3>
            <LineChart :data="monthlyMessageData" />
          </div>
        </div>
      </div>
    </div>
  </template>

<script setup>
import { useUserStore } from '@/stores/users'
import { useConversationsStore } from '@/stores/conversations'
import { computed, onMounted } from 'vue'
import LineChart from '@/components/dashboard/LineChart.vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { creditsLeftFormatted } from '@/utils/userUtils'

const { t } = useI18n({ useScope: 'global' })
const userStore = useUserStore()
const conversationStore = useConversationsStore()
const router = useRouter()

const creditsLeft = computed(() => creditsLeftFormatted(userStore.billingCreditsLeft))

onMounted(() => {
  if (conversationStore.conversations.length === 0) {
    conversationStore.fetchConversations()
  }
  if (!userStore.user?.stats) {
    userStore.fetchUserStats()
  }
})

const totalMessages = computed(() => {
  return userStore.user?.stats?.monthly_usage?.reduce((sum, monthlyUsage) => {
    return sum + monthlyUsage.usage
  }, 0) || 0
})

const monthlyMessageData = computed(() => {
  const monthlyUsage = userStore.user?.stats?.monthly_usage || []

  // Sort monthlyUsage lexicographically by month
  const sortedMonthlyUsage = [...monthlyUsage].sort((a, b) => a.month.localeCompare(b.month))

  const months = sortedMonthlyUsage.map(monthlyUsage => monthlyUsage.month)
  const usages = sortedMonthlyUsage.map(monthlyUsage => monthlyUsage.usage)

  return {
    labels: months,
    datasets: [
      {
        label: t('dashboard.chartLabel'),
        data: usages,
        fill: false,
        borderColor: '#085f63',
        backgroundColor: '#49beb7',
        tension: 0.4
      }
    ]
  }
})

const redirectToPricing = () => {
  router.push('/pricing')
}
</script>

<style scoped>
</style>
